import { FC, ReactElement } from 'react';
import classnames from 'classnames';

interface TooltipWrapperProps {
  tooltip: string;
  children: ReactElement;
  id?: string;
  className?: string;
  position?: 'top' | 'top-left' | 'bottom' | 'bottom-left';
  showTooltip?: boolean;
  customTooltipClassName?: string;
  /** When true, tooltip text may wrap; default is single-line (nowrap). */
  allowMultilineTooltip?: boolean;
}

export const TooltipWrapper: FC<TooltipWrapperProps> = ({
  tooltip,
  children,
  id,
  className,
  position = 'top',
  showTooltip = true,
  customTooltipClassName = '',
  allowMultilineTooltip = false
}) => {
  const bodySurfaceClass = allowMultilineTooltip
    ? 'w-[min(260px,calc(100vw-2rem))] break-words border border-borderGray px-3 py-2 text-left text-xs font-normal ' +
      'leading-snug text-primaryText shadow-md'
    : 'px-2 py-1 text-xs font-normal text-primaryText shadow-md';
  const isTopPosition = position === 'top' || position === 'top-left';
  const isBottomPosition = position === 'bottom' || position === 'bottom-left';
  const isMultilineBottomLeft = position === 'bottom-left' && allowMultilineTooltip;

  return (
    <div className={`group relative z-0 hover:z-50 ${className}`} id={id}>
      {children}
      {showTooltip && (
        <div
          className={classnames(
            'absolute z-50 hidden rounded bg-white',
            allowMultilineTooltip ? '' : 'whitespace-nowrap',
            bodySurfaceClass,
            isTopPosition && 'bottom-full mb-3',
            position === 'bottom' && 'top-full left-1/2 mt-3 -translate-x-1/2',
            isMultilineBottomLeft && 'top-full left-0 mt-3',
            position === 'bottom-left' && !allowMultilineTooltip && '-bottom-8 translate-x-[-93%]',
            position === 'top' && 'left-1/2 -translate-x-1/2',
            position === 'top-left' && 'left-0',
            'before:absolute before:h-0 before:w-0 before:border-4 before:border-x-transparent before:content-[""]',
            isTopPosition && 'before:top-[100%] before:border-t-white before:border-b-transparent',
            isBottomPosition && 'before:bottom-[100%] before:border-b-white before:border-t-transparent',
            position === 'top' && 'before:left-1/2 before:-translate-x-1/2',
            position === 'top-left' && 'before:left-2',
            position === 'bottom' && 'before:left-1/2 before:-translate-x-1/2',
            isMultilineBottomLeft && 'before:left-2',
            position === 'bottom-left' && !allowMultilineTooltip && 'before:left-[93%]',
            'group-hover:block',
            customTooltipClassName
          )}
          role="tooltip"
          aria-hidden="true">
          {tooltip}
        </div>
      )}
    </div>
  );
};
